import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as Yup from "yup";
import {
    Card,
    CardBody,
    Row,
    Col,
    Form,
    Input,
    Button,
    Label,
    FormGroup,
    Container,
    FormFeedback,
} from "reactstrap";
import Loaders from "components/Common/Loaders";
import { urlEndPoint } from "helpers/GeneralHelper";
import {
    addAIAddonCallTemplate,
    getAIAddonCallTemplateById,
    getAIAddonCallTemplateDefinitions,
    updateAIAddonCallTemplate,
} from "model/AIAddonCallTemplateModel";

const initialFormState = {
    name: "",
    note: "",
    body: "",
    statusId: "",
    isActive: true,
};

const aiAddonCallTemplateSchema = Yup.object().shape({
    name: Yup.string().trim().required("Please select a template"),
    note: Yup.string().trim(),
    body: Yup.string().trim().required("Body is required"),
});

const formatDataTag = (propertyName = "") => `[{code:'${propertyName}'}]`;

const ACTIVE_FIELD_LABELS = {
    note: "Note",
    body: "Body",
};

const resolveTemplateItemName = (item = {}) =>
    String(item?.name ?? item?.Name ?? item?.templateName ?? "").trim();

const mapDetailToFormState = (detail = {}, resolvedTemplateName = "") => {
    const statusValue = String(detail?.statusId ?? detail?.status ?? "").toLowerCase();

    return {
        name: resolvedTemplateName,
        note: detail?.note ?? "",
        body: detail?.body ?? detail?.Body ?? "",
        statusId: detail?.statusId ?? "",
        isActive: statusValue === "active" || statusValue === "1" || statusValue === "true",
    };
};

const findDefinitionByTemplateName = (definitionList = [], templateName = "") => {
    const normalizedTemplateName = String(templateName || "").trim().toLowerCase();
    if (!normalizedTemplateName) return null;

    return (
        definitionList.find(
            (definition) =>
                String(definition?.name || "").trim().toLowerCase() === normalizedTemplateName
        ) || null
    );
};

const AddEditAIAddonTemplate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: templateId } = useParams();
    const isEditMode = Boolean(templateId);

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [templateItemName, setTemplateItemName] = useState("");
    const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
    const [definitions, setDefinitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeField, setActiveField] = useState("body");
    const [statusFormKey, setStatusFormKey] = useState(0);

    const matchedDefinition = useMemo(
        () => findDefinitionByTemplateName(definitions, templateItemName),
        [definitions, templateItemName]
    );

    const dataTags = Array.isArray(matchedDefinition?.properties)
        ? matchedDefinition.properties
        : [];

    const templateSelectOptions = useMemo(
        () =>
            definitions
                .filter((definition) => definition?.enable !== false)
                .map((definition) => {
                    const optionName = resolveTemplateItemName(definition);
                    return {
                        value: optionName,
                        label: definition?.description
                            ? `${optionName} - ${definition.description}`
                            : optionName,
                    };
                }),
        [definitions]
    );

    const getSelectedOption = (options, value) =>
        options.find((option) => String(option?.value) === String(value)) || null;

    useEffect(() => {
        const bootstrap = async () => {
            setLoading(true);
            try {
                const definitionsResponse = await getAIAddonCallTemplateDefinitions({});
                const definitionList = definitionsResponse?.data || [];
                const normalizedDefinitions = Array.isArray(definitionList) ? definitionList : [];
                setDefinitions(normalizedDefinitions);

                const nameFromNavigation = resolveTemplateItemName({
                    name: location?.state?.templateName,
                });

                if (templateId) {
                    const detailResponse = await getAIAddonCallTemplateById(templateId);
                    const detail = detailResponse?.data || {};
                    const resolvedTemplateName =
                        resolveTemplateItemName(detail) || nameFromNavigation;

                    setTemplateItemName(resolvedTemplateName);
                    setSelectedTemplateKey(resolvedTemplateName);
                    setFormData(mapDetailToFormState(detail, resolvedTemplateName));
                    setErrors({});
                    setStatusFormKey((key) => key + 1);
                } else if (nameFromNavigation) {
                    setTemplateItemName(nameFromNavigation);
                    setSelectedTemplateKey(nameFromNavigation);
                    setFormData((previous) => ({
                        ...previous,
                        name: nameFromNavigation,
                    }));
                }
            } catch (error) {
                toast.error(error?.message || "Unable to load AI Add On Call template");
            } finally {
                setLoading(false);
            }
        };

        bootstrap();
    }, [templateId, location?.state?.templateName]);

    const clearFieldError = (fieldName) => {
        setErrors((previous) => {
            if (!previous?.[fieldName]) return previous;
            return { ...previous, [fieldName]: "" };
        });
    };

    const applySelectedTemplate = (selectedDefinition) => {
        if (!selectedDefinition) {
            setTemplateItemName("");
            setFormData(initialFormState);
            setErrors({});
            setStatusFormKey((key) => key + 1);
            return;
        }

        const resolvedTemplateName = resolveTemplateItemName(selectedDefinition);
        setTemplateItemName(resolvedTemplateName);
        setFormData({
            ...initialFormState,
            name: resolvedTemplateName,
        });
        clearFieldError("name");
        setStatusFormKey((key) => key + 1);
    };

    const handleTemplateSelect = (selectedOption) => {
        const nextKey = selectedOption?.value || "";
        setSelectedTemplateKey(nextKey);

        if (!nextKey) {
            applySelectedTemplate(null);
            return;
        }

        const selectedDefinition = findDefinitionByTemplateName(definitions, nextKey);
        applySelectedTemplate(selectedDefinition);
    };

    const handleStatusChange = (isActive) => {
        setFormData((previous) => ({
            ...previous,
            isActive,
            statusId: isActive ? "active" : "deactive",
        }));
    };

    const handleChange = async (event) => {
        const { name, value, type, checked } = event.target;
        const fieldValue = type === "checkbox" ? checked : value;

        setFormData((previous) => ({
            ...previous,
            [name]: fieldValue,
        }));

        if (!aiAddonCallTemplateSchema.fields[name]) return;

        try {
            await Yup.reach(aiAddonCallTemplateSchema, name).validate(fieldValue);
            clearFieldError(name);
        } catch (error) {
            setErrors((previous) => ({ ...previous, [name]: error.message }));
        }
    };

    const insertIntoTextField = (fieldName, tagText) => {
        setFormData((previous) => ({
            ...previous,
            [fieldName]: `${previous?.[fieldName] || ""}${tagText}`,
        }));
        clearFieldError(fieldName);
    };

    const insertTag = (propertyName) => {
        const tagText = formatDataTag(propertyName);

        if (activeField === "note") {
            insertIntoTextField("note", tagText);
            return;
        }

        insertIntoTextField("body", tagText);
    };

    const resolveSelectedDefinitionName = () =>
        String(
            matchedDefinition?.name ||
                selectedTemplateKey ||
                formData?.name ||
                templateItemName ||
                ""
        ).trim();

    const buildSubmitPayload = () => {
        const statusId = formData?.isActive ? "active" : "deactive";
        const note = String(formData?.note || "").trim();
        const body = String(formData?.body || "").trim();

        if (isEditMode) {
            return { note, body, statusId };
        }

        return {
            name: resolveSelectedDefinitionName(),
            note,
            body,
            statusId,
        };
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const payload = buildSubmitPayload();
        const validationPayload = {
            name: resolveSelectedDefinitionName(),
            note: String(formData?.note || "").trim(),
            body: String(formData?.body || "").trim(),
        };

        try {
            await aiAddonCallTemplateSchema.validate(validationPayload, { abortEarly: false });
            setErrors({});
        } catch (error) {
            const nextErrors = {};
            error.inner?.forEach((item) => {
                nextErrors[item.path] = item.message;
            });
            setErrors(nextErrors);
            return;
        }

        const swalResult = await Swal.fire({
            title: "Are you sure?",
            text: isEditMode
                ? "Do you want to update this AI Add On Call template?"
                : "Do you want to save this AI Add On Call template?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!swalResult?.isConfirmed) return;

        setSaving(true);
        try {
            const response = isEditMode
                ? await updateAIAddonCallTemplate(templateId, payload)
                : await addAIAddonCallTemplate(payload);

            if (response?.success) {
                toast.success(
                    response?.message ||
                        (isEditMode
                            ? "AI Add On Call template updated successfully"
                            : "AI Add On Call template saved successfully")
                );
                navigate(`/${urlEndPoint.AIAddonCallTemplateSetting}`);
            } else {
                toast.error(response?.message || "Unable to save AI Add On Call template");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loaders />
                </Container>
            </div>
        );
    }

    const activeFieldLabel = ACTIVE_FIELD_LABELS[activeField] || "Body";

    return (
        <div className="page-content mail-template-editor-page">
            <Container fluid>
                <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="fw-bold mb-1">
                                    {isEditMode
                                        ? "Edit AI Add On Call Template"
                                        : "Add AI Add On Call Template"}
                                </h5>
                                {templateItemName ? (
                                    <strong className="text-muted mb-0">{templateItemName}</strong>
                                ) : null}
                            </div>
                            <Button
                                color="light"
                                className="border"
                                type="button"
                                onClick={() => navigate(`/${urlEndPoint.AIAddonCallTemplateSetting}`)}
                            >
                                Back
                            </Button>
                        </div>
                        <hr />

                        <Form onSubmit={handleSubmit}>
                            <Row className="g-4">
                                <Col lg={8}>
                                    {!isEditMode ? (
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>
                                                        Select Template{" "}
                                                        <span className="text-danger">*</span>
                                                    </Label>
                                                    <Select
                                                        options={templateSelectOptions}
                                                        className={`select2-selection${
                                                            errors.name ? " is-invalid" : ""
                                                        }`}
                                                        placeholder="Select Template"
                                                        value={getSelectedOption(
                                                            templateSelectOptions,
                                                            selectedTemplateKey
                                                        )}
                                                        onChange={handleTemplateSelect}
                                                        isClearable
                                                    />
                                                    {errors.name ? (
                                                        <small className="text-danger">
                                                            {errors.name}
                                                        </small>
                                                    ) : null}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    ) : null}

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>Note</Label>
                                                <Input
                                                    type="text"
                                                    name="note"
                                                    value={formData.note}
                                                    onChange={handleChange}
                                                    onFocus={() => setActiveField("note")}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>
                                                    Body <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="textarea"
                                                    name="body"
                                                    rows={14}
                                                    value={formData.body}
                                                    onChange={handleChange}
                                                    onFocus={() => setActiveField("body")}
                                                    invalid={Boolean(errors.body)}
                                                />
                                                {errors.body ? (
                                                    <FormFeedback>{errors.body}</FormFeedback>
                                                ) : null}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup
                                                key={`ai-addon-call-template-status-${statusFormKey}`}
                                            >
                                                <Label>Status</Label>
                                                <div className="input-box d-flex align-items-center justify-content-between">
                                                    <span className="text-muted">Choose Status</span>
                                                    <div>
                                                        <Input
                                                            type="radio"
                                                            name="isActive"
                                                            defaultChecked={formData.isActive}
                                                            onChange={() => handleStatusChange(true)}
                                                        />{" "}
                                                        Active
                                                        <Input
                                                            type="radio"
                                                            name="isActive"
                                                            className="ms-3"
                                                            defaultChecked={!formData.isActive}
                                                            onChange={() =>
                                                                handleStatusChange(false)
                                                            }
                                                        />{" "}
                                                        Deactive
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="text-end mt-3">
                                        <Button color="primary" type="submit" disabled={saving}>
                                            {isEditMode ? "Update" : "Save"}
                                        </Button>
                                    </div>
                                </Col>

                                <Col lg={4}>
                                    <Card className="mail-template-tag-panel border-0 shadow-sm h-100">
                                        <CardBody className="mail-template-tag-panel__body">
                                            <div className="mail-template-tag-panel__header">
                                                <div>
                                                    <h6 className="mail-template-tag-panel__title mb-0">
                                                        <i
                                                            className="fa fa-tag me-2"
                                                            aria-hidden="true"
                                                        />
                                                        Data Tags
                                                    </h6>
                                                    <p className="mail-template-tag-panel__hint mb-0">
                                                        Double-click a tag to insert it.
                                                    </p>
                                                </div>
                                                <span className="mail-template-tag-panel__count">
                                                    {dataTags.length}
                                                </span>
                                            </div>

                                            <div className="mail-template-active-field">
                                                <span className="mail-template-active-field__label">
                                                    Inserting into
                                                </span>
                                                <span className="mail-template-active-field__value">
                                                    {activeFieldLabel}
                                                </span>
                                            </div>

                                            {!templateItemName ? (
                                                <div className="mail-template-tag-empty">
                                                    Select a template to load data tags.
                                                </div>
                                            ) : !dataTags.length ? (
                                                <div className="mail-template-tag-empty">
                                                    No data tags found for{" "}
                                                    <strong>{templateItemName}</strong>.
                                                </div>
                                            ) : (
                                                <div className="mail-template-tag-list">
                                                    {dataTags.map((propertyName) => (
                                                        <button
                                                            key={propertyName}
                                                            type="button"
                                                            className="mail-template-tag-chip"
                                                            onDoubleClick={() =>
                                                                insertTag(propertyName)
                                                            }
                                                            title={`Insert ${formatDataTag(
                                                                propertyName
                                                            )}`}
                                                        >
                                                            <span className="mail-template-tag-chip__label">
                                                                {propertyName}
                                                            </span>
                                                            <span className="mail-template-tag-chip__code">
                                                                {formatDataTag(propertyName)}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default AddEditAIAddonTemplate;
